<template>
  <div class="groups bg-white h-full">
    <FilterBar @searchChanged="setSearchValue">
      <template v-slot:filter>
        <div>
          <ClassDropdown
            :classSelected="filterValue.group"
            @classValueChanged="updateFilteredClass"
          ></ClassDropdown>
        </div>
        <div>
          <button
            type="button"
            class="filter-bar-button focus:outline-none hover:opacity-75"
            v-bind:class="filterHasValues ? 'filter-item-button-on' : 'bg-white'"
            @click="showFilter()"
          >
            <i class="fas fa-filter"></i> Filters
          </button>
        </div>
      </template>

      <template v-slot:actions>
        <div v-if="pupilsAreSelected">
          <button
            type="button"
            class="
              filter-bar-button
              text-gray-800
              hover:opacity-75
              focus:outline-none
            "
            @click="showMovePupils = true"
          >
            <i class="fas fa-compress"></i>
            Move Pupils
          </button>

          <button
            type="button"
            class="
              filter-bar-button
              text-gray-800
              hover:opacity-75
              focus:outline-none
              bg-red-400
              text-green-100
            "
            @click="deletePupils()"
          >
            <i class="fas fa-trash"></i>
            Delete Pupils
          </button>
        </div>


        <div class="mr-2" v-if="!pupilsAreSelected">
          <button
            type="button"
            class="
              filter-bar-button
              text-gray-800
              hover:opacity-75
              focus:outline-none
              disabled:opacity-50 disabled:cursor-not-allowed
            "
            @click="showImport = true"
            :disabled="shouldShowUpgrade"
            
          >
            <i class="fas fa-file-import"></i>
            Import
          </button>
        </div>

        <div>
          <button
            type="button"
            class="action-button hover:opacity-75
            disabled:opacity-50 disabled:cursor-not-allowed"
            @click="showAddPupil = true"
                :disabled="shouldShowUpgrade"
          >
            <i class="fas fa-plus"></i>
            Add New
          </button>
        </div>
      </template>
    </FilterBar>

    <PupilFilterModal
      class=""
      v-if="filterIsVisible"
      :values="filterValue"
      @apply-filter="applyFilter"
      @close="showFilter"
    >
    </PupilFilterModal>

    <p v-if="showMessage" class="save-notice ml-6 mb-6 font-bold">
      {{ msg }} <i class="far fa-check-circle"></i>
    </p>

    <table
      class="
        w-95p
        mx-auto
        mr-8
        mb-2
        h-10
        pt-2
        align-text-bottom
        bg-white
        rounded
        text-left text-gray-400
      "
    >
      <thead class="bg-gray-100 w-full">
        <tr>
          <th class="text-xl px-3 cursor-pointer w-2" @click="setSelectAll()">
            <i
              class="far"
              v-bind:class="[
                selectAll
                  ? 'fa-check-circle text-orange-300'
                  : 'fa-circle text-gray-200',
              ]"
            ></i>
          </th>
          <th
            v-for="(col, index) in columns"
            :key="index"
            class="pl-2"
            v-bind:class="col.css"
            @click="sort(col.shortcode)"
          >
            {{ col.label }}
            <i
              v-if="col.shortcode.length > 0"
              class="fas fa-sort tbh-sort"
              v-bind:class="
                col.shortcode === sortBy ? 'text-blue-500' : 'text-gray-400'
              "
            ></i>
          </th>
        </tr>
      </thead>
    </table>

    <div class="table-wrapper">
      <div v-if="errored" class="empty">
        <p>
          We are not able to retrieve this information at the moment, please try
          back later
        </p>
      </div>

      <div id="content-area" v-else>
        <div
          style="width: 100%; text-align: center"
          class="spin-holder mt-4"
          v-if="loading"
        >
          <i class="spinner fa fa-spinner fa-spin"></i>
          <p>Loading Pupils</p>
        </div>

        <div v-else>
          <table
            v-if="componentLoaded"
            class="table-wrapper mx-auto bg-white w-95p rounded shadow-sm"
          >
            <tbody>
              <tr v-for="pupil in filteredList" :key="pupil.identifier">
                <td
                  class="w-2 text-lg cursor-pointer"
                  @click="selectPupil(pupil)"
                >
                  <i
                  class="px-4"
                    v-bind:class="[
                      isSelected(pupil)
                        ? 'fas fa-check-circle text-orange-300'
                        : ' far fa-circle text-gray-200',
                    ]"
                  ></i>
                </td>
                <td class="w-2/12">
                  {{ pupilYearGroupDisplay(pupil) }}
                </td>
                <td class="w-3/12">{{ pupil.name }}</td>
                <td class="w-2/12">{{ pupil.inclass }}</td>
                <td class="w-1/12 text-center">
                  <i
                    class="icon list"
                    v-bind:class="setOnClass(pupil.premium_pupil)"
                  />
                </td>
                <td class="w-1/12 text-center">
                  <i
                    class="icon list"
                    v-bind:class="setOnSEN(pupil.sen_status)"
                  />
                </td>
                <td class="w-1/12 text-center">
                  <i
                    class="icon list"
                    v-bind:class="setOnClass(pupil.english_second)"
                  />
                </td>
                <td
                  class="
                    w-1/12
                    text-center text-xs
                    cursor-pointer
                    text-orange-400
                    hover:opacity-50
                  "
                >
                  <a @click="selectPupilTimeline(pupil)">Timeline</a>
                </td>
                <td
                  class="
                    w-1/12
                    text-center text-xs
                    cursor-pointer
                    text-blue-400
                    hover:opacity-50
                  "
                >
                  <a @click="editPupil(pupil)">View</a>
                </td>
              </tr>
            </tbody>
          </table>

          <p class="m-5 mx-12" v-else>
            We could not find any data for your given selection.
          </p>
        </div>

        <div  class="w-95p mx-auto">
        <Pagination
          v-if="componentLoaded"
          :total-pages="totalPages"
          :total="totalResultsCount"
          :current-page="currentPage"
          :has-more-pages="hasMorePages"
          :pageSize="pageSize"
          @pagechanged="showMore"
          class="mt-4 mb-12"
        >
        </Pagination>
        </div>
      </div>
    </div>

    <!--------- IMPORT ------------>
    <ModalSlide :show="showImport" @closeModal="closeImport">
      <template v-slot:header-icon>
        <i class="fas fa-upload"></i>
      </template>
      <template v-slot:header-title>
        Pupil Import
        <div class="text-xs">
          Upload and save pupils to the system from our
     
          <button
          class="hover:opacity-75 text-blue-500 underline"
      role="link"
      @click="openInNewTab('https://primarypepassport.co.uk/downloads/import_template.csv')"
    >
    CSV template.
    </button>
        </div>
      </template>
      <template v-slot:body>
        <PupilImport></PupilImport>
      </template>
    </ModalSlide>

    <!--------- ADD NEW ------------>

    <ModalSlide :show="showAddPupil" @closeModal="closeAddPupil">
      <template v-slot:header-icon>
        <i class="fas fa-plus"></i>
      </template>
      <template v-slot:header-title>
        Add New Pupil
        <div class="text-xs">Enter details for new pupil.</div>
      </template>
      <template v-slot:body>
        <PupilAdd :years="years" @pupil-added="pupilAdded"></PupilAdd>
      </template>
    </ModalSlide>


      <!--------- MOVE ------------>

      <ModalSlide :show="showMovePupils" @closeModal="closeMovePupils">
      <template v-slot:header-icon>
        <i class="fas fa-compress"></i>
      </template>
      <template v-slot:header-title>
        Move Pupils
        <div class="text-xs">Move pupils between year groups and classes.</div>
      </template>
      <template v-slot:body>
        <PupilMove :pupils="selectedPupils" @pupil-moved="pupilMoved" v-if="selectedPupils.length > 0 && showMovePupils"></PupilMove>
      </template>
    </ModalSlide>

  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { PupilService } from "@/services/pupil.service";
import { yearGroupMixin } from "@/mixins/yearGroupMixin.js";
import { upgradeMixin } from "@/mixins/upgradeMixin.js";


const PupilImport = () => import("@/components/pupil/Import.vue");
const PupilAdd = () => import("@/components/pupil/PupilAdd.vue");
const PupilMove = () => import("@/components/pupil/PupilMove.vue");

const ClassDropdown = () => import("@/components/pupil/ClassDropdown.vue");

const ModalSlide = () => import("@/components/ModalSlide.vue");
const Pagination = () => import("@/components/helpers/Pagination.vue");
const PupilFilterModal = () => import("@/components/helpers/PupilFilter.vue");
const FilterBar = () => import("@/components/FilterBarNavigation.vue");

export default {
  name: "pupils",
  mixins: [yearGroupMixin, upgradeMixin],
  props: {
    msg: { type: String, required: false },
  },
  computed: {
    ...mapGetters({
      currentUser: "currentUser",
      currentAccess: "currentAccess",
    }),
    hasMorePages() {
      return this.pagination.has_more === true;
    },
    totalResultsCount() {
      return this.pagination.total_count;
    },
    totalPages() {
      return this.pagination.total_pages;
    },
    componentLoaded() {
      return this.filteredList.length > 0;
    },
    showMessage() {
      return this.msg != undefined && this.msg.length > 0;
    },
    searchValue() {
      return this.search;
    },
    pupilsAreSelected() {
      return this.selectedPupils.length > 1;
    },
    filteredList() {
      let vm = this;
      return _.orderBy(vm.pupils, [vm.sortBy, "name"], [vm.sortDirection]);
    },
    filterHasValues() {
      var value = this.filterValue;
      if (
        value.group != undefined ||
        value.years != undefined ||
        value.types != undefined ||
        (value.tags != undefined && value.length > 2)
      ) {
        return true;
      }
    },
    classSelectedValue() {
      return this.filterValue.group;
    },
  },
  data() {
    return {
      publicPath: process.env.BASE_URL,
      filterIsVisible: false,
      pupils: [],

      loading: false,
      errored: false,

      showMovePupils: false,
      selectedPupils: [],

      showImport: false,

      showAddPupil: false,

      page: 1,
      pageSize: 48,
      currentPage: 1,
      pagination: {},

      search: "",
      filterValue: {},

      selectAll: false,

      sortBy: "yeargroup",
      sortDirection: "asc",

      columns: [
        { label: "Year Group", shortcode: "yeargroup", css: "w-2/12" },
        { label: "Name", shortcode: "name", css: "w-3/12 text-left" },
        { label: "Class", shortcode: "inclass", css: "w-2/12" },
        { label: "PP", shortcode: "premium_pupil", css: "w-1/12 text-center" },
        { label: "SEN", shortcode: "sen_status", css: "w-1/12 text-center" },
        {
          label: "EAL",
          shortcode: "english_second",
          css: "w-1/12 text-center",
        },
        { label: "", shortcode: "", css: "w-1/12" },
        { label: "", shortcode: "", css: "w-1/12" },
      ], // columns
    };
  },
  created() {
     
  },
  mounted() {
    this.searchPupils();
  },
  methods: {
    searchPupils() {
      this.loading = true;

      PupilService.getWithFilter(this.filterValue, this.page, this.pageSize)
        .then((response) => this.buildPupils(response.data))
        .catch((err) => {
          console.log("pupils " + err);
          this.errored = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    buildPupils(data) {
      this.pupils = data.data;
      this.pagination = data.meta.pagination;
    },
    selectPupil(pupil) {
      if (this.selectedPupils.some((p) => p.identifier === pupil.identifier)) {
        this.selectedPupils.splice(
          this.selectedPupils.findIndex(
            (item) => item.identifier === pupil.identifier
          ),
          1
        );
      } else {
        this.selectedPupils.push(pupil);
      }
    },
    isSelected(pupil) {
      if (this.selectedPupils.some((p) => p.identifier === pupil.identifier)) {
        return true;
      } else {
        return false;
      }
    },
    setSelectAll() {
      this.selectAll = !this.selectAll;
      if (this.selectAll == false) {
        this.selectedPupils = [];
      } else {
        this.selectedPupils = [];
        this.selectedPupils = this.selectedPupils.concat(this.filteredList);
      }
    },
    editPupil(pupil) {
      this.$router.push({
        name: "PupilEdit",
        params: {
          pupil: pupil,
          newAddition: false,
          years: this.years,
        },
      });
      window.scrollTo(0, 0);
    },
    setOnClass(value) {
      if (value === 1) {
        return "fas fa-check-circle text-green-400";
      } else {
        return "fas fa-times-circle text-gray-300";
      }
    },
    setOnSEN(value) {
      let result = this.parseSEN(value) === true ? 1 : 0
      return this.setOnClass(result)
    }, 
    parseSEN(sen) {
      if (sen === undefined || sen === null) {
        return false
      }

      return sen.length > 3 ? true : false
    },
    showFilter() {
      this.filterIsVisible = !this.filterIsVisible;
    },
    updateFilteredClass(value) {
      if (value != undefined) {
        this.filterValue.group = value;
      } else {
        this.filterValue.group = {};
      }

      this.showMore({ page: 1, size: this.pageSize });
    },
    applyFilter(filters) {
      this.filterValue = filters;
      this.updateFilteredClass(filters.group);

      this.showFilter();
      this.showMore({ page: 1, size: this.pageSize });
    },
    closeMovePupils() {
      this.showMovePupils = false;
      this.isSaving = false;
      //window.location.reload()
    },
    closeImport() {
      this.showImport = false;
    },
    closeAddPupil() {
      this.showAddPupil = false;
    },
    pupilAdded() {
      this.showMore({ page: 1, size: this.pageSize });
    },
    pupilMoved() {
      this.closeMovePupils()
      this.selectedPupils = []
      this.showMore({ page: 1, size: this.pageSize });
    },
    setSearchValue(value, time = 300) {
      this.search = value;
      this.filterValue.title = this.search;

      let vm = this;
      if (vm.search.length === 0 || vm.search.length >= 1) {
        clearTimeout(vm.timeout);
        vm.timeout = setTimeout(() => {
          vm.showMore({ page: 1, size: this.pageSize });
        }, time);
      }
    },
    showMore({ page, size }) {
      this.page = page;
      this.currentPage = page;
      this.pageSize = size;
      this.searchPupils();
      window.scrollTo(0, 0);
    },
    sort(s) {
      if (s === this.sortBy) {
        this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
      }
      this.sortBy = s;
    },
    openInNewTab(url) {
      window.open(url, '_blank', 'noreferrer');
    },
    deletePupils() {
      this.$swal({
        title: "Are you sure you want to delete?",
        text: "You will not be able to revert this!",
        type: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete!",
      }).then((result) => {
        if (result.value === true) {
          this.processDelete();
        }
      });
    },
    processDelete() {
      let vm = this;
      vm.selectedPupils.forEach(function (pp, index) {
        PupilService.delete(pp)
        .then((response) => {
            if (index == vm.selectedPupils.length - 1) {
              vm.pupilMoved()
            }
          })
        .catch((err) => {
          console.log(err);
        });
      })
    },
    selectPupilTimeline(pupil) {
      this.$router.push({
        name: "TimelineView",
        params: { pupil: pupil, calendar: this.filterValue.calendar, pathBack: "toolbox/pupils"},
      });
      window.scrollTo(0, 0);
    },
  },
  components: {
    PupilFilterModal,
    FilterBar,
    Pagination,
    ModalSlide,
    PupilImport,
    PupilAdd,
    ClassDropdown,
    PupilMove,
  },
};
</script>

<style>
i.icon {
  font-size: 18px;
  padding-left: 4px;
  padding-top: 4px;
}

.move-pupil-btn:disabled {
  cursor: not-allowed;
  opacity: 0.4;
}
</style>
